/**
 * Writing the catalogue.
 *
 * Mirrors `usePartyWrites` exactly, including the reason: every write opens its
 * own transaction with `products` AND `syncOutbox` in scope, so a saved item and
 * the change that publishes it either both happen or neither does. An item saved
 * without its outbox record exists on this till and on no other, and nothing
 * reports the gap.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { resolveActiveBusiness } from '@data/services/businessSession';
import {
  createProduct,
  deleteProduct,
  updateProduct,
  type CreateProductInput,
} from '@data/services/productService';
import { PRODUCTS_QUERY_ROOT } from './useProducts';
import { MASTER_DATA_DEPENDENT_ROOTS } from '@features/purchaseOrders/usePurchaseOrders';

const PRODUCT_WRITE_SCOPE = ['products', 'syncOutbox'] as const;

/**
 * Refreshing only the product lists is not enough.
 *
 * Every document editor prefills a rate from the product it just picked. Those
 * editors keep their options under their OWN query roots, so an invalidation that
 * stops at `PRODUCTS_QUERY_ROOT` leaves an open Purchase Order (or Sales) Order
 * prefilling the price as it was BEFORE the edit — with no visible reason, and a
 * total that quietly disagrees with the catalogue. The user would have to know to
 * reopen the screen.
 *
 * Imported from `usePurchaseOrders` rather than redeclared here, so the list has one
 * definition rather than one per writer.
 */
async function invalidateProducts(client: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    client.invalidateQueries({ queryKey: PRODUCTS_QUERY_ROOT }),
    ...MASTER_DATA_DEPENDENT_ROOTS.map((queryKey) => client.invalidateQueries({ queryKey })),
  ]);
}

export interface UseWriteProductOptions {
  readonly onDone?: () => void;
}

export function useCreateProduct({ onDone }: UseWriteProductOptions = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Omit<CreateProductInput, 'businessId' | 'user' | 'now' | 'deviceId'>,
    ) => {
      const db = getDb();
      const business = await resolveActiveBusiness(db);
      const device = await currentDeviceId(db);
      return db.transaction('rw', PRODUCT_WRITE_SCOPE.map((n) => db.table(n)), () =>
        createProduct(db as never, {
          ...input,
          businessId: business.id,
          user: { id: 'local', name: 'You' },
          now: new Date().toISOString(),
          deviceId: device,
        }),
      );
    },
    onSuccess: async () => {
      await invalidateProducts(client);
      onDone?.();
    },
  });
}

export function useUpdateProduct({ onDone }: UseWriteProductOptions = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      /** Only the fields being changed. The service merges with the stored row. */
      input: Partial<Omit<CreateProductInput, 'user' | 'now' | 'deviceId'>>;
    }) => {
      const db = getDb();
      const business = await resolveActiveBusiness(db);
      const device = await currentDeviceId(db);
      return db.transaction('rw', PRODUCT_WRITE_SCOPE.map((n) => db.table(n)), () =>
        updateProduct(db as never, {
          ...input,
          id,
          businessId: business.id,
          user: { id: 'local', name: 'You' },
          now: new Date().toISOString(),
          deviceId: device,
        }),
      );
    },
    onSuccess: async () => {
      await invalidateProducts(client);
      onDone?.();
    },
  });
}

/**
 * Deactivate an item. NOT a hard delete — see the service header: a historical
 * invoice must still describe the item it was sold for, and an offline device
 * must not be able to resurrect the row.
 */
export function useDeactivateProduct({ onDone }: UseWriteProductOptions = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const db = getDb();
      const business = await resolveActiveBusiness(db);
      const device = await currentDeviceId(db);
      return db.transaction('rw', PRODUCT_WRITE_SCOPE.map((n) => db.table(n)), () =>
        deleteProduct(db as never, {
          id,
          businessId: business.id,
          now: new Date().toISOString(),
          deviceId: device,
        }),
      );
    },
    onSuccess: async () => {
      await invalidateProducts(client);
      onDone?.();
    },
  });
}